import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Row, Col, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import Select from "react-select";
import statusColors from "../utils/statusColors";
import { settlementJangad, isErpApiSuccess } from "../erpClient";
import { createJangadIdempotencyKey } from "../utils/jangadsErpAdapter";
import {
  getTodayDateString,
  getReceiveDateInputProps,
  validateReceiveDate,
} from "../utils/receiveDateInput";

function JangadReceiveUpdateModal({
  show,
  onHide,
  orderData,
  onReceiveSuccess,
  masterGoldRate = 0,
  masterSilverRate = 0,
}) {
  const [loading, setLoading] = useState(false);
  const [receiveDate, setReceiveDate] = useState(getTodayDateString);

  // Product groups and receive data
  const [productGroups, setProductGroups] = useState([]);
  const [itemReceiveData, setItemReceiveData] = useState({});
  const settlementIdempotencyKeyByItemRef = useRef({});

  // Live rate is always enabled
  const showLiveRate = true;

  // Payment mode options for React Select
  const PAYMENT_MODE_OPTIONS = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "Bank Transfer", label: "Bank Transfer" },
    { value: "Cheque", label: "Cheque" },
    { value: "Card", label: "Card" },
  ];

  // Utility for per gram rate calculation (consistent with Karigar.jsx)
  const calculatePerGramRate = (rate) => rate / 10;
  const calculatePerGramSilverRate = (rate) => rate / 1000;

  // Metal payment mode options for Metal in Metal
  const METAL_PAYMENT_MODE_OPTIONS = [
    { value: "Gold", label: "Gold" },
    { value: "Silver", label: "Silver" },
  ];

  // Listen for API URL changes and close modal
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in JangadReceiveUpdateModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  // Reset idempotency keys when modal closes; lock receive date to today when opened
  useEffect(() => {
    if (!show) {
      settlementIdempotencyKeyByItemRef.current = {};
    } else {
      setReceiveDate(getTodayDateString());
    }
  }, [show]);

  // Fetch product groups on component mount
  useEffect(() => {
    if (show && orderData) {
      // Use custom_order_entry if available (prepared by JangadTable), otherwise jangad_entries
      const sourceEntries =
        orderData.custom_order_entry &&
        Array.isArray(orderData.custom_order_entry)
          ? orderData.custom_order_entry
          : orderData.jangad_entries && Array.isArray(orderData.jangad_entries)
          ? orderData.jangad_entries
          : [];

      if (sourceEntries.length > 0) {
        const groups = sourceEntries.map((item) => {
          return {
            id: item.id,
            jangad_details_id: item.jangad_details_id,
            product_group: item.product_group,
            product_group_name: item.product_group_name || "",
            tag_no: item.tag_no,
            pcs: item.pcs,
            gwt: item.gwt,
            nwt: item.nwt,
            itrate: item.itrate,
            lbr_type: item.lbr_type,
            lbr_percent: item.lbr_percent,
            lbr_rate: item.lbr_rate,
            lbr_amt: item.lbr_amt,
            totalamt: item.totalamt,
            status: item.status,
            // Received fields
            received_by: item.received_by,
            received_no_of_visitors: item.received_no_of_visitors,
            received_visitor_names: item.received_visitor_names,
            received_visitor_mobiles: item.received_visitor_mobiles,
            received_remarks: item.received_remarks,
            received_gwt: item.received_gwt,
            received_nwt: item.received_nwt,
            // Jangad items don't have assigned karigar
            assigned_karigar_id: null,
            assigned_karigar_name: null,
            assigned_karigar_company: null,
          };
        });
        setProductGroups(groups);

        // Initialize receive data for each item
        const initialReceiveData = {};
        groups.forEach((item) => {
          initialReceiveData[item.id] = {
            received_gwt: "",
            received_nwt: "",
            received_no_of_visitors: "",
            received_visitor_names: "",
            received_visitor_mobiles: "",
            received_remarks: "",
          };
        });
        setItemReceiveData(initialReceiveData);
      }
    }
  }, [show, orderData]);

  const handleReceiveUpdate = async () => {
    const receiveDateError = validateReceiveDate(receiveDate, setReceiveDate);
    if (receiveDateError) {
      toast.error(receiveDateError);
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.allSettled(
        Object.entries(itemReceiveData).map(async ([itemId]) => {
          const itemData = productGroups.find(
            (group) => group.id.toString() === itemId.toString(),
          );
          if (!itemData) return { skipped: true };

          const isReturned = itemData.status === "2" || itemData.status === 2;
          if (isReturned) return { skipped: true };

          const existingKey = settlementIdempotencyKeyByItemRef.current[itemId];
          const idempotency_key =
            existingKey ||
            (settlementIdempotencyKeyByItemRef.current[itemId] =
              createJangadIdempotencyKey());

          const received_gwt = parseFloat(
            itemReceiveData[itemId]?.received_gwt || 0,
          );
          const received_nwt = parseFloat(
            itemReceiveData[itemId]?.received_nwt || 0,
          );
          const received_no_of_visitors = parseFloat(
            itemReceiveData[itemId]?.received_no_of_visitors || 0,
          );
          const received_visitor_names =
            itemReceiveData[itemId]?.received_visitor_names || "";
          const received_visitor_mobiles =
            itemReceiveData[itemId]?.received_visitor_mobiles || "";
          const received_remarks = itemReceiveData[itemId]?.received_remarks || "";

          return settlementJangad({
            idempotency_key,
            jangad_item_id: parseInt(itemId, 10),
            received_date: receiveDate,
            // Also send weights when provided (backend can ignore if not supported)
            received_gwt: Number.isFinite(received_gwt) ? received_gwt : 0,
            received_nwt: Number.isFinite(received_nwt) ? received_nwt : 0,
            received_no_of_visitors: Number.isFinite(received_no_of_visitors)
              ? received_no_of_visitors
              : 0,
            received_visitor_names,
            received_visitor_mobiles,
            received_remarks,
          });
        }),
      );

      const rejected = results.filter((r) => r.status === "rejected");
      const failed = results.filter(
        (r) =>
          r.status === "fulfilled" &&
          !r.value?.skipped &&
          !isErpApiSuccess(r.value),
      );

      if (rejected.length === 0 && failed.length === 0) {
        toast.success("Jangad items received successfully!");
        onReceiveSuccess && onReceiveSuccess();
      } else {
        const firstError =
          rejected[0]?.reason?.message ||
          failed[0]?.value?.message ||
          "Some jangad settlement operations failed. Please try again.";
        toast.error(firstError);
      }
    } catch (error) {
      console.error("❌ Error receiving jangad items:", error);
      console.error("❌ Error response data:", error.response?.data);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to receive jangad items";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleItemFieldChange = (itemId, field, value) => {
    setItemReceiveData((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Receive & Update Jangad Items</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Receive Date */}
          <Row className="mb-3">
            <Col md={2}>
              <Form.Label>Receive Date</Form.Label>
              <Form.Control
                {...getReceiveDateInputProps(receiveDate, (e) =>
                  setReceiveDate(e.target.value),
                )}
              />
            </Col>
          </Row>

          {/* Product Groups and Item Receive Data */}
          {productGroups.length > 0 && (
            <div className="mb-4">
              {productGroups.map((item, index) => (
                <div key={item.id} className="mb-4 border rounded p-3">
                  <div className="mb-3">
                    <h6 className="mb-0 d-flex align-items-center gap-2 flex-wrap">
                      <span>
                        Item {index + 1}:{" "}
                        {item.product_group_name ||
                          `Product Group ID: ${item.product_group}`}
                      </span>
                      {/* Jangad items don't have karigar info */}

                      {/* Original GWT and NWT badges */}
                      {item.gwt && (
                        <span className={`badge ${statusColors.info}`}>
                          GWT: {parseFloat(item.gwt).toFixed(2)}g
                        </span>
                      )}
                      {item.nwt && (
                        <span className={`badge ${statusColors.info}`}>
                          NWT: {parseFloat(item.nwt).toFixed(2)}g
                        </span>
                      )}
                    </h6>
                  </div>

                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Label>Received GWT</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Received GWT"
                        value={itemReceiveData[item.id]?.received_gwt || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_gwt",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Received NWT</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter Received NWT"
                        value={itemReceiveData[item.id]?.received_nwt || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_nwt",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>No. of Visitors</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        placeholder="Enter Visitors"
                        value={
                          itemReceiveData[item.id]?.received_no_of_visitors ||
                          ""
                        }
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_no_of_visitors",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Visitor Names</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Visitor Names"
                        value={
                          itemReceiveData[item.id]?.received_visitor_names || ""
                        }
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_visitor_names",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Visitor Mobiles</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Visitor Mobiles"
                        value={
                          itemReceiveData[item.id]?.received_visitor_mobiles ||
                          ""
                        }
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_visitor_mobiles",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label>Received Remarks</Form.Label>
                      <Form.Control
                        placeholder="Enter any remarks for this receive..."
                        value={itemReceiveData[item.id]?.received_remarks || ""}
                        onChange={(e) =>
                          handleItemFieldChange(
                            item.id,
                            "received_remarks",
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleReceiveUpdate}
          disabled={!receiveDate || loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Submitting...
            </>
          ) : (
            <>Submit</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default JangadReceiveUpdateModal;
